import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import apiCalls from '../utils/apiCalls';
import utils from '../utils/utils';
import GeneralForm from '../components/generalForm';
import AutoResizeTextarea from '../components/customFields/autoResizeTextarea';
import GooglePlacesField from '../components/customFields/googlePlacesField';
import circlePurple from '../landing/assets/landing-1-circle-purple.svg';
import circleGreen from '../landing/assets/landing-1-circle-green.svg';
import CustomSelect from '../components/customFields/customSelect';
import { getServiceAreas } from '../serviceAreas/serviceAreasActions';

function UserForm({
  currentUser,
  intl,
  getServiceAreas,
  serviceAreas,
  history,
  isOnboarding,
  location,
  match,
  onlyForm,
  userId,
  onSuccess,
}) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    if (userId || (match && match.params.userId)) {
      setUser(null);
      setLoading(true);
      setError(null);

      apiCalls
        .getUsersAdmin({
          userId: userId || match.params.userId,
        })
        .then((result) => {
          if (result.data && result.data.users.length) {
            setUser(result.data.users[0]);
            setAvatar(result.data.users[0].avatar);
            setLoading(false);
          }
        })
        .catch((errors) => {
          setError(utils.getErrorMessageFromResponse(errors, true));
          setLoading(false);
        });
    } else {
      setUser(currentUser);
      setAvatar(currentUser.avatar);
      setLoading(false);
    }
  }, [userId, match]);

  useEffect(() => {
    getServiceAreas();
  }, [getServiceAreas]);

  const serviceAreaOptions = serviceAreas
    ? serviceAreas.map((area) => {
        return {
          value: area.id,
          label: `${area.name}`,
        };
      })
    : [];

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      firstName: {
        type: 'string',
        title: intl.formatMessage({ id: 'user.form.field.firstName.label' }),
      },
      lastName: {
        type: 'string',
        title: intl.formatMessage({ id: 'user.form.field.lastName.label' }),
      },
      phoneNumber: {
        type: 'string',
        title: intl.formatMessage({ id: 'user.form.field.phone.number.label' }),
      },
      contact: {
        type: 'string',
        title: intl.formatMessage({ id: 'user.form.field.contact.label' }),
      },
      serviceAreaId: {
        type: 'string',
        title: intl.formatMessage({ id: 'user.form.field.service.area.label' }),
        options: serviceAreaOptions,
      },
      address: {
        type: 'object',
        required: [],
        properties: {
          unit: {
            type: 'string',
            default: '',
          },
          street: {
            type: 'string',
            default: '',
          },
          city: {
            type: 'string',
            default: '',
          },
          country: {
            type: 'string',
            default: '',
          },
          postCode: {
            type: 'string',
            default: '',
          },
          coordinates: {
            type: 'object',
            required: [],
            properties: {
              lat: {
                type: 'string',
              },
              lon: {
                type: 'string',
              },
            },
          },
        },
      },
      promotionalEmailsDisabled: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'user.form.field.promotional.emails.disabled.label' }),
      },
      obfuscateData: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'user.form.field.obfuscate.data.label' }),
      },
      passwordChangeToken: {
        type: 'string',
        title: intl.formatMessage({ id: 'user.form.field.password.change.token.label' }),
      },
    },
  };

  const uiSchema = {
    address: {
      'ui:field': 'googlePlacesField',
    },
    serviceAreaId: {
      'ui:field': 'customSelect',
    },
  };

  const uploadUserAvatar = (image) => {
    return apiCalls.uploadImage(image, 'user', user.id);
  };

  const deleteUserAvatar = (image) => {
    return apiCalls.deleteImage(image);
  };

  const onUserAvatarChange = (uploadedImages) => {
    setAvatar(uploadedImages[0]);
  };

  const onFormSubmit = () => {
    setLoading(true);

    apiCalls
      .editUser({ user: user, image: avatar })
      .then((response) => {
        onFormSuccess(response.data.user);
      })
      .catch((error) => {
        setError(utils.getErrorMessageFromResponse(error, true));
        setLoading(false);
        window.scrollTo(0, 0);
      });
  };

  const onFormSuccess = (user) => {
    const redirectUrl = location && location.state ? location.state.redirectUrl : null;

    if (onlyForm) {
      onSuccess();
      return;
    }

    if (redirectUrl) {
      history.push({
        pathname: redirectUrl,
      });
    } else {
      history.push({
        pathname: '/pood',
      });
    }
  };

  const formFields = {
    autoResizeTextarea: AutoResizeTextarea,
    googlePlacesField: GooglePlacesField,
    customSelect: CustomSelect,
  };

  const buttonCopy = 'user.edit.form.submit';

  const images = avatar ? [avatar] : [];

  const header = !isOnboarding ? intl.formatMessage({ id: 'user.edit.title' }) : undefined;

  const userForm = user ? (
    <GeneralForm
      title={header}
      onChange={(event) => setUser(event.formData)}
      onSubmit={onFormSubmit}
      uploadImage={uploadUserAvatar}
      deleteImage={deleteUserAvatar}
      onImagesChange={onUserAvatarChange}
      formSchema={formSchema}
      formUiSchema={uiSchema}
      formFields={formFields}
      formData={user}
      uploadedImages={images}
      buttonCopy={buttonCopy}
      errorMessage={error}
      loading={loading}
      hideImageUpload={isOnboarding}
      singleImage={true}
    />
  ) : (
    ''
  );

  return onlyForm ? (
    <div>{userForm}</div>
  ) : (
    <div className="content-page">
      <img className="content-page__circle-purple" src={circlePurple} alt="" />
      <img className="content-page__circle-green" src={circleGreen} alt="" />

      <div className="container content-page__container">
        <div className="row">
          <div className="col-12 col-sm-10 offset-sm-1 col-md-8 offset-md-2">
            {isOnboarding ? (
              <>
                <h2>
                  <FormattedMessage id={'user.onboarding.title'} />
                </h2>
                <p>
                  <FormattedMessage id={'user.onboarding.description'} />
                </p>
              </>
            ) : (
              ''
            )}

            {userForm}
          </div>
        </div>
      </div>
    </div>
  );
}

UserForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onboard: PropTypes.bool,
  onlyForm: PropTypes.bool.isRequired,
};

UserForm.defaultProps = {
  onboard: false,
  onlyForm: false,
  onSuccess: () => {},
};

function mapStateToProps(state) {
  return {
    serviceAreas: state.serviceAreas.serviceAreas,
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {
  getServiceAreas,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(UserForm));
